import React, {
  Dispatch,
  FC,
  KeyboardEvent,
  RefObject,
  SetStateAction
} from "react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { State } from "../../enums";
import { useThemeColors } from "../../hooks/useThemeColors/useThemeColors";
import PauseIcon from "../../icons/PauseIcon";
import PlayIcon from "../../icons/PlayIcon";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";

import AudioTimeLine from "./AudioTimeline/AudioTimeLine";
import AudioVolume from "./AudioVolume/AudioVolume";
import { PlaybackRateToggle } from "./PlaybackRateToggle/PlaybackRateToggle";
import Share from "./Share/Share";
import { AudioStrippedButton, IconCircleWrapper } from "./AudioPlayerButtons";
import { AudioPlayerPreviewButton } from "./AudioPlayerPreviewButton";

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 40px;
  width: 100%;
`;

const TimeLineWrapper = styled.div<{
  showVolumeSlider: boolean;
}>`
  align-items: center;
  display: flex;
  flex-grow: ${props => (props.showVolumeSlider ? 4 : 8)};
  transition: flex-grow 400ms;
`;

interface Props {
  audio?: HTMLAudioElement;
  containerRef: RefObject<HTMLDivElement | null>;
  setShowVolumeSlider: Dispatch<SetStateAction<boolean>>;
  showVolumeSlider: boolean;
  state: State;
}

export const AudioPlayerControls: FC<Props> = ({
  audio,
  containerRef,
  setShowVolumeSlider,
  showVolumeSlider,
  state
}) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const error = useStoreState.error();
  const isLive = useStoreState.isLive();
  const isPaused = useStoreState.isPaused();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const player = useStoreState.player();

  const { language, playButton, sharing, theme } = useEmbedParametersContext();
  const { title: audioTitle } = useMediaContext();
  const themeColors = useThemeColors(theme);

  const { baseColor, fillColor, hoverColor } = themeColors;

  const handlePreviewClick = () => {
    containerRef.current?.focus();
    player.play();
  };

  const handlePreviewKeyDown = (event: KeyboardEvent) => {
    if (event.key === " ") {
      handlePreviewClick();
    }
  };

  return (
    <>
      {!isPlaybackStarted && playButton && (
        <AudioPlayerPreviewButton
          onClick={handlePreviewClick}
          onKeyDown={handlePreviewKeyDown}
          state={error ? State.ERROR : state}
        />
      )}

      {audio && isPlaybackStarted && (
        <>
          {isPaused ? (
            <AudioStrippedButton
              aria-label={i18n("toista_aria_label", { language })}
              isMobile={device.isMobile}
              margin="0 2px 0 0"
              onClick={() => player.play()}>
              <IconCircleWrapper
                backgroundColor={fillColor}
                hoverColor={hoverColor}>
                <PlayIcon fillColor={baseColor} />
              </IconCircleWrapper>
            </AudioStrippedButton>
          ) : (
            <AudioStrippedButton
              aria-label={i18n("keskeyta_aria_label", { language })}
              isMobile={device.isMobile}
              margin="0 2px 0 0"
              onClick={player.pause}>
              <IconCircleWrapper
                backgroundColor={fillColor}
                hoverColor={hoverColor}>
                <PauseIcon fillColor={baseColor} />
              </IconCircleWrapper>
            </AudioStrippedButton>
          )}
          <Container>
            <TimeLineWrapper showVolumeSlider={showVolumeSlider}>
              <AudioTimeLine themeColors={themeColors} />
            </TimeLineWrapper>
            {!isLive && (
              <PlaybackRateToggle
                language={language}
                themeColors={themeColors}
              />
            )}
            <AudioVolume
              language={language}
              setShowVolumeSlider={setShowVolumeSlider}
              showVolumeSlider={showVolumeSlider}
              themeColors={themeColors}
            />
            {!isLive && sharing && (
              <Share
                audioTitle={audioTitle}
                fillColor={fillColor}
                language={language}
              />
            )}
          </Container>
        </>
      )}
    </>
  );
};
